<template>
  <div class="flex flex-col gap-4 text-left" data-testid="pin-step-2">
    <div class="flex flex-wrap items-center gap-2">
      <LabelBadge
        size="small"
        :icon="null"
        :color="confidenceColor"
        :label="confidenceLabel"
        data-testid="pin-confidence-badge"
      />
      <span class="text-sm text-gray-600">{{ pointName }}</span>
    </div>

    <p class="text-sm text-gray-500">Arraste o marcador até a entrada usada pelos médicos.</p>

    <div class="relative h-72 w-full overflow-hidden rounded-xl border border-gray-100">
      <GoogleMaps
        v-if="point"
        :latitude="point.lat"
        :longitude="point.lng"
        :radius="geo.reachRadius.value != null ? Number(geo.reachRadius.value) : undefined"
        :address="geo.formattedAddress.value"
        :points="doctorPoints"
        :fit-to-points="false"
        :map-type="mapType"
        :show-info="false"
        @marker-dragged="geo.updateCoordinates"
      />
      <div v-else class="flex h-full items-center justify-center text-sm text-gray-500">
        Sem coordenadas para exibir. Volte ao passo 1 e escolha um ponto.
      </div>
    </div>

    <div class="flex flex-wrap items-center justify-between gap-2">
      <div class="inline-flex overflow-hidden rounded-lg border border-gray-200">
        <button
          type="button"
          class="px-3 py-1.5 text-sm"
          :class="mapType === 'roadmap' ? 'bg-[#2F7F91] text-white' : 'bg-white text-gray-600'"
          @click="emit('update:mapType', 'roadmap')"
          data-testid="pin-map-type-roadmap"
        >
          <SVGIcon :path="mdiMap" class="mr-1 inline-block" />Mapa
        </button>
        <button
          type="button"
          class="px-3 py-1.5 text-sm"
          :class="mapType === 'satellite' ? 'bg-[#2F7F91] text-white' : 'bg-white text-gray-600'"
          @click="emit('update:mapType', 'satellite')"
          data-testid="pin-map-type-satellite"
        >
          <SVGIcon :path="mdiSatelliteVariant" class="mr-1 inline-block" />Satélite
        </button>
      </div>

      <p class="flex items-center gap-3 text-xs text-gray-500">
        <span class="flex items-center gap-1">
          <span class="h-2.5 w-2.5 rounded-full" :style="{ backgroundColor: ACCEPTED_COLOR }" />
          check aceito
        </span>
        <span class="flex items-center gap-1">
          <span class="h-2.5 w-2.5 rounded-full" :style="{ backgroundColor: REJECTED_COLOR }" />
          rejeitado
        </span>
        <span class="flex items-center gap-1">
          <span class="h-2.5 w-2.5 rounded-full border border-primary" />
          raio
        </span>
      </p>

      <p class="font-mono text-xs text-gray-600" data-testid="pin-coordinates">{{ coordinates }}</p>
    </div>

    <p v-if="isLoadingPoints" class="text-xs text-gray-500" data-testid="pin-points-loading">
      Carregando os checks dos médicos dos últimos 14 dias...
    </p>

    <div
      v-if="showDoctorsCard"
      class="rounded-xl border border-gray-100 bg-white p-4"
      data-testid="pin-doctors-card"
    >
      <p class="text-sm font-semibold text-gray-800">
        <template v-if="distanceToDoctorsM != null">
          Os médicos realizam o check a {{ formatMeters(distanceToDoctorsM) }} do pino atual
        </template>
        <template v-else>Checks dos médicos nos últimos 14 dias</template>
      </p>
      <p v-if="doctorSummary" class="mt-1 text-sm text-gray-500">
        {{ doctorSummary.total }} checks de {{ doctorSummary.doctors }}
        {{ doctorSummary.doctors === 1 ? 'médico' : 'médicos' }} em {{ doctorSummary.days }}
        {{ doctorSummary.days === 1 ? 'dia' : 'dias' }}.
      </p>

      <div class="mt-3 flex flex-wrap gap-2">
        <FilledButton
          text="Centralizar nos médicos"
          width="auto"
          height="40px"
          class="!w-auto !px-4 text-sm"
          :prepend-icon="mdiCrosshairsGps"
          @click="emit('center-doctors')"
          data-testid="pin-center-doctors-button"
        />
        <OutlinedButton
          v-if="geo.canResetLocation.value"
          text="Voltar ao pino atual"
          width="auto"
          height="40px"
          class="!w-auto !px-4 text-sm"
          @click="geo.resetLocation()"
          data-testid="pin-reset-location-button"
        />
      </div>
    </div>

    <div class="rounded-xl border border-gray-100 bg-white p-4">
      <p class="flex items-center gap-1.5 text-sm font-semibold text-gray-800">
        <SVGIcon :path="mdiMapMarkerRadiusOutline" class="text-base text-primary" />
        Raio de alcance · preset {{ unitTypeLabel(geo.unitType.value) }}
      </p>

      <div class="mt-3 flex flex-wrap items-center gap-2">
        <button
          v-for="chip in RADIUS_CHIPS"
          :key="chip"
          type="button"
          class="rounded-full border px-3 py-1 text-sm"
          :class="
            geo.reachRadius.value === chip
              ? 'border-[#2F7F91] bg-[#2F7F91] text-white'
              : 'border-gray-200 bg-white text-gray-600'
          "
          @click="geo.setReachRadius(chip)"
          :data-testid="`pin-radius-chip-${chip}`"
        >
          {{ chip }} m
        </button>
        <button
          type="button"
          class="rounded-full border border-gray-200 bg-white px-3 py-1 text-sm text-gray-600"
          @click="focusRadiusInput()"
          data-testid="pin-radius-chip-other"
        >
          Outro
        </button>
      </div>

      <div ref="radiusFieldRef" class="mt-3 w-full sm:max-w-[220px]">
        <GenericInput
          type="number"
          label="Raio (m)"
          :model-value="geo.reachRadius.value"
          @update:model-value="geo.setReachRadius($event)"
          :rules="reachRadiusRules"
          :min="REACH_RADIUS_MIN"
          :max="REACH_RADIUS_MAX"
          :hint="REACH_RADIUS_HINT"
          persistent-hint
          data-testid="reach-radius-input"
        />
      </div>

      <p
        v-if="geo.isRadiusOffPreset.value"
        class="mt-2 text-sm text-alert"
        data-testid="pin-radius-off-preset-warning"
      >
        Fora do preset do tipo ({{ geo.radiusPreset.value }} m). Justifique no próximo passo.
      </p>

      <p
        v-if="originalRadiusOverMax"
        class="mt-2 text-sm text-negative"
        data-testid="pin-radius-over-max-warning"
      >
        Raio atual é {{ formatRadius(geo.originalRadius.value) }} e escondia o erro. Acima de
        {{ REACH_RADIUS_MAX }} m não é permitido.
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { ValidationError } from 'yup'
import { mdiCrosshairsGps, mdiMap, mdiMapMarkerRadiusOutline, mdiSatelliteVariant } from '@mdi/js'
import GoogleMaps from '@/components/GoogleMaps.vue'
import SVGIcon from '@/components/SVGIcon.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import GenericInput from '@/components/designSystem/Inputs/GenericInput.vue'
import LabelBadge from '@/components/designSystem/Badges/LabelBadge.vue'
import type { LabelBadgeColor } from '@/components/designSystem/Badges/LabelBadge.vue'
import { ACCEPTED_COLOR, REJECTED_COLOR } from '@/components/CheckAttempts/checkAttemptsFormat'
import { formatMeters, haversineMeters, type LatLng } from '@/utils/geo'
import {
  HealthUnitSchema,
  REACH_RADIUS_HINT,
  REACH_RADIUS_MAX,
  REACH_RADIUS_MIN
} from '../../schemas'
import { RADIUS_CHIPS, unitTypeLabel } from '../../unitType'
import type { UnitGeolocationApi } from '../../composables/useUnitGeolocation'
import type { DoctorPoint } from './pinModalTypes'

type DoctorSummary = { total: number; rejected: number; doctors: number; days: number } | null

const props = defineProps<{
  geo: UnitGeolocationApi
  mapType: 'roadmap' | 'satellite'
  doctorPoints: DoctorPoint[]
  doctorSummary: DoctorSummary
  isLoadingPoints: boolean
}>()

const emit = defineEmits<{
  (e: 'update:mapType', value: 'roadmap' | 'satellite'): void
  (e: 'center-doctors'): void
}>()

const radiusFieldRef = ref<HTMLElement | null>(null)

const point = computed(() => props.geo.currentPoint.value)

const coordinates = computed(() =>
  point.value ? `${point.value.lat.toFixed(6)}, ${point.value.lng.toFixed(6)}` : '—'
)

const confidenceColor = computed<LabelBadgeColor>(() => {
  if (props.geo.origin.value === 'SUGGESTION') return 'blue'
  return props.geo.confidence.value === 'BUILDING' ? 'green' : 'yellow'
})

const confidenceLabel = computed(() => {
  if (props.geo.origin.value === 'SUGGESTION') return 'Sugestão dos checks'
  if (props.geo.confidence.value === 'BUILDING') return 'Prédio identificado (Google Places)'
  return 'Endereço aproximado'
})

const pointName = computed(() => props.geo.placeName.value || props.geo.formattedAddress.value)

/** Centro da nuvem de checks — o alvo do "Centralizar nos médicos" quando não há sugestão. */
const doctorsCentroid = computed<LatLng | null>(() => {
  if (!props.doctorPoints.length) return null
  const sum = props.doctorPoints.reduce(
    (acc, doctorPoint) => ({ lat: acc.lat + doctorPoint.lat, lng: acc.lng + doctorPoint.lng }),
    { lat: 0, lng: 0 }
  )
  return { lat: sum.lat / props.doctorPoints.length, lng: sum.lng / props.doctorPoints.length }
})

const doctorsTarget = computed(() => props.geo.suggestedPoint.value ?? doctorsCentroid.value)

const distanceToDoctorsM = computed(() =>
  point.value && doctorsTarget.value ? haversineMeters(point.value, doctorsTarget.value) : null
)

const showDoctorsCard = computed(
  () => !!props.geo.suggestedPoint.value || !!props.doctorSummary || !!props.doctorPoints.length
)

const originalRadiusOverMax = computed(
  () => (props.geo.originalRadius.value ?? 0) > REACH_RADIUS_MAX
)

/** Raio grande em metros cheios ("3.000 m"): em km o número perde a comparação com o preset. */
function formatRadius(meters: number | undefined): string {
  return meters == null ? '—' : `${meters.toLocaleString('pt-BR')} m`
}

function focusRadiusInput(): void {
  radiusFieldRef.value?.querySelector('input')?.focus()
}

/** Campo vazio não acusa erro: o gate é o "Continuar" do rodapé. */
const isBlank = (value: unknown) => value === '' || value === null || value === undefined

/**
 * Delega ao schema do formulário: limites e mensagens ficam num só lugar, iguais
 * às que o submit e o backend usariam para o mesmo valor.
 */
const reachRadiusRules = [
  (v: unknown) => {
    if (isBlank(v)) return true

    try {
      HealthUnitSchema.validateSyncAt('reachRadius', { reachRadius: v })
      return true
    } catch (error) {
      return error instanceof ValidationError ? error.message : REACH_RADIUS_HINT
    }
  }
]
</script>
